import { Ref, useImperativeHandle, useMemo } from 'react';

import { Address } from '@ravago/shared/page-data/models/elements/address.model';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { RadioGroup } from '@ravago/shared/radiance/components/radio/components/RadioGroup/RadioGroup';
import { Tooltip } from '@ravago/shared/radiance/components/tooltip/components/Tooltip/Tooltip';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { Controller, useForm } from 'react-hook-form';

import { LeadTimeCheckCriteria, hasMtsLocalLeadTime } from '../../../../../../../utils/lead-time';
import { RequestForQuotationStep } from '../../../../models/request-for-quotation-step';
import { RfqAddressPicker } from '../fields/rfq-address-picker';
import { RfqTimeframeOptions } from '../fields/rfq-timeframe-options';

interface LocalFormValue {
   timeframe: DropdownOption | undefined;
   deliveryAddress: Address | undefined;
}

export interface FormValue {
   timeframe: DropdownOption;
   deliveryAddress: Address;
}

interface Props {
   stepRef: Ref<RequestForQuotationStep>;
   addresses: Address[];
   defaultFormValue: FormValue | undefined;
   onSubmit: (value: FormValue) => void;
   leadTimeIndicators?: LeadTimeIndicator[];
   selectedColor?: { colorName: string; producerColorCode: string | undefined; colorId?: number };
   packagingGroupId?: string;
   isLeadTimeEnabled?: boolean;
}

export const RfqFlyoutStepAddressDetails = ({
   stepRef,
   addresses,
   defaultFormValue,
   onSubmit,
   leadTimeIndicators = [],
   selectedColor,
   packagingGroupId,
   isLeadTimeEnabled = false
}: Props) => {
   const translate = useTranslations();

   const deliveryAddresses = addresses.filter((address) => address.type === 'Delivery Address');

   const groupedAddresses = useMemo(() => {
      if (!isLeadTimeEnabled || !selectedColor?.colorId || Number.isNaN(Number(packagingGroupId)) || leadTimeIndicators.length === 0) {
         return { mtsLocal: [], other: deliveryAddresses };
      }

      const mtsLocalAddresses: Address[] = [];
      const otherAddresses: Address[] = [];

      deliveryAddresses.forEach((address) => {
         const criteria: LeadTimeCheckCriteria = {
            colorId: selectedColor.colorId!,
            producerColorCode: selectedColor.producerColorCode,
            packagingGroupId: Number(packagingGroupId),
            addressId: address.id
         };

         if (hasMtsLocalLeadTime(leadTimeIndicators, criteria)) {
            mtsLocalAddresses.push(address);
         } else {
            otherAddresses.push(address);
         }
      });

      return { mtsLocal: mtsLocalAddresses, other: otherAddresses };
   }, [deliveryAddresses, packagingGroupId, leadTimeIndicators, selectedColor, isLeadTimeEnabled]);

   const { control, handleSubmit } = useForm<LocalFormValue, unknown, FormValue>({
      defaultValues: defaultFormValue ?? {
         timeframe: undefined,
         deliveryAddress: deliveryAddresses.find((address) => address.isDefault)
      }
   });

   useImperativeHandle(stepRef, () => ({
      submit: async () => {
         await handleSubmit((value) => onSubmit(value))();
      }
   }));

   return (
      <Box direction="column">
         <RfqTimeframeOptions control={control} name="timeframe" />

         <Box gap="md" direction="column">
            <Box direction="row" gap="2xs" items="center">
               <Typography component="span" variant="body-highlight">
                  {translate('page-consumer.request-for-quotation.delivery')}
               </Typography>
               <Tooltip align="start" pointers text={translate('page-consumer.request-for-quotation.tooltip.delivery')}>
                  <Box>
                     <Icon icon="info-circle" />
                  </Box>
               </Tooltip>
            </Box>
            <Box gap="md" direction="column">
               <Controller
                  control={control}
                  name="deliveryAddress"
                  rules={{
                     required: translate('form-validation.required', { field: translate('form-field.address') })
                  }}
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                     <RadioGroup {...field} errorMessage={error?.message}>
                        {isLeadTimeEnabled ? (
                           <Box direction="column">
                              {groupedAddresses.mtsLocal.length > 0 && (
                                 <Box gap="md" direction="column">
                                    <Box direction="row" gap="xs" items="center">
                                       <Icon icon="truck" color="primary" />
                                       <Typography variant="small-highlight" color="primary">
                                          {translate('request-for-quotation.lead-time.mts-local-days')}
                                       </Typography>
                                    </Box>
                                    {groupedAddresses.mtsLocal.map((address) => (
                                       <RfqAddressPicker key={address.id} address={address} />
                                    ))}
                                 </Box>
                              )}
                              {groupedAddresses.other.length > 0 && (
                                 <Box gap="md" direction="column">
                                    <Box direction="row" gap="xs" items="center">
                                       <Icon icon="truck" color="primary" />
                                       <Typography variant="small-highlight" color="primary">
                                          {translate('request-for-quotation.lead-time.delivery-on-request')}
                                       </Typography>
                                    </Box>
                                    {groupedAddresses.other.map((address) => (
                                       <RfqAddressPicker key={address.id} address={address} />
                                    ))}
                                 </Box>
                              )}
                           </Box>
                        ) : (
                           <Box direction="column" gap="xs">
                              {deliveryAddresses.map((address) => (
                                 <RfqAddressPicker key={address.id} address={address} />
                              ))}
                           </Box>
                        )}
                     </RadioGroup>
                  )}
               />
            </Box>
         </Box>
      </Box>
   );
};
